import classNames from 'classnames'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { AddMoreButton } from 'legacy/lut/components/widgets/public/partner360/commitment/addMoreBtn/AddMoreButton'
import { CommitmentItemEditor } from 'legacy/lut/components/widgets/public/partner360/commitment/CommitmentItemEditor'
import {
  CommitmentTypes,
  ICommitmentEditorProps,
} from 'legacy/lut/components/widgets/public/partner360/commitment/models'
import { AlertBlock } from 'legacy/lut/components/widgets/public/partner360/SignOffAlert'
import { selectManagerHeaderKey } from 'legacy/lut/features/partner360/configuration/selectors/selectManagerHeaderKey'
import { selectPartnerHeaderKey } from 'legacy/lut/features/partner360/configuration/selectors/selectPartnerHeaderKey'
import { selectPbmHeaderKey } from 'legacy/lut/features/partner360/configuration/selectors/selectPbmHeaderKey'
import { useJbpSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { ICommitmentData } from 'legacy/lut/services/partner360/commitment/models/apiCommitmentData'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { FlexDirectionBreak } from 'core-components/sitebuilder/models/flexGrid/FlexDirectionBreak'
import MeasureController from 'core/analytics/MeasureController'

import { CommitmentStatusIcon } from '../../editors/CommitmentStatusIcon'
import { HelpSection } from '../../helpButton'
import { WidgetHeaderWithHelpButton } from '../../widgetHeader'
import { getCommitmentGroupStatusInfo } from '../helpers/getCommitmentStatusInfo'
import { SkeletonLoader } from '../loader/SkeletonLoader'

import styles from './CommitmentTypeGroup.module.scss'

interface IProps {
  itemClassName?: string
  commitmentType: CommitmentTypes
  commitmentItems: ICommitmentData[]
  getProps: (commitmentItem: ICommitmentData) => ICommitmentEditorProps
  flexWidth: FlexDirectionBreak
  pid: string
  metrics: MeasureController
  fyear: number
  partnerId: string
  setIsRefuseCurrentItem: (value: boolean) => void
  programType: ProgramTypes
  checkContactIsUnique: (email: string) => boolean
}

export const CommitmentTypeGroup = ({
  itemClassName,
  commitmentType,
  pid,
  commitmentItems,
  getProps,
  metrics,
  flexWidth,
  partnerId,
  fyear,
  setIsRefuseCurrentItem,
  programType,
  checkContactIsUnique,
}: IProps) => {
  const pbmHeaderKey = useSelector(selectPbmHeaderKey)
  const managerHeaderKey = useSelector(selectManagerHeaderKey)
  const partnerHeaderKey = useSelector(selectPartnerHeaderKey)
  const { jbpSettings } = useJbpSettings()
  const { isPartnerUser } = useUserRoles()
  const [loaded, setLoaded] = useState(false)

  const getHeaderText = useCallback(
    (commitmentType: CommitmentTypes) => {
      if (commitmentType === CommitmentTypes.Manager) {
        return managerHeaderKey
      }
      if (commitmentType === CommitmentTypes.PBM) {
        return pbmHeaderKey
      }
      return partnerHeaderKey
    },
    [managerHeaderKey, partnerHeaderKey, pbmHeaderKey]
  )

  const getHintText = useCallback((commitmentType: CommitmentTypes) => {
    if (commitmentType === CommitmentTypes.Manager) {
      return HelpSection.ManagerAcknowledgment
    }
    if (commitmentType === CommitmentTypes.PBM) {
      return HelpSection.PBMAcknowledgment
    }
    return HelpSection.PartnerAcknowledgment
  }, [])

  const items = commitmentItems.filter((el) => el.CommitmentType === commitmentType)

  const headerText = useMemo(() => getHeaderText(commitmentType), [getHeaderText, commitmentType])
  const tooltipText = useMemo(() => getHintText(commitmentType), [getHintText, commitmentType])
  const commitmentGroupStatus = useMemo(() => getCommitmentGroupStatusInfo(items), [items])

  const pbmList = useMemo(
    () =>
      commitmentItems
        .filter((el) => el.CommitmentType === CommitmentTypes.PBM)
        .map((el) => el.SignerEmail || '')
        .filter(Boolean),
    [commitmentItems]
  )

  useEffect(() => {
    if (commitmentItems && !loaded) {
      setLoaded(true)
    }
  }, [loaded, commitmentItems])

  if (!loaded) {
    return <SkeletonLoader cols={1} />
  }

  return (
    <>
      <div className={classNames(styles.header, itemClassName)}>
        <CommitmentStatusIcon
          className={styles.headerIcon}
          icon={`${commitmentGroupStatus.icon}`}
          color={commitmentGroupStatus.color}
          size="normal"
        />
        <WidgetHeaderWithHelpButton section={tooltipText}>{headerText}</WidgetHeaderWithHelpButton>
      </div>
      {items.map((ackItem: ICommitmentData, idx: number) => (
        <div key={idx} className={classNames(styles.card, itemClassName)}>
          <div className={styles.flexible}>
            <CommitmentItemEditor key={idx} {...getProps(ackItem)} />
            {/* ------------------------------ Alert message Block ------------------------------ */}
            {!!ackItem.AlertKey && (
              <div className={styles.bottom}>
                <AlertBlock text={ackItem.AlertKey} />
              </div>
            )}
          </div>
        </div>
      ))}
      <div className={itemClassName}>
        <RenderWhen condition={!isPartnerUser && fyear >= jbpSettings.currentFYear}>
          <AddMoreButton
            widgetParams={{}}
            ackPartialInfo={{
              FYear: fyear,
              PartnerId: partnerId,
              CommitmentType: commitmentType,
              ProgramType: programType,
            }}
            commitmentType={commitmentType}
            metrics={metrics}
            flexWidth={flexWidth}
            partnerId={partnerId}
            pbmList={pbmList}
            setIsRefuseCurrentItem={setIsRefuseCurrentItem}
            pid={pid}
            checkContactIsUnique={checkContactIsUnique}
          />
        </RenderWhen>
      </div>
    </>
  )
}
